import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { currentSubscription, disablePush, enablePush, pushSupported } from "@/lib/push";
import { logError } from "@/lib/log";

export const SupporterHeartAlerts = ({ token, name }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/supporters/view/${token}`;

  useEffect(() => {
    Promise.all([api.get(`${base}/push`), currentSubscription().catch(() => null)])
      .then(([r, sub]) => setS({ ...r.data, here: !!sub && r.data.devices > 0 }))
      .catch((e) => logError("Heart alerts load failed:", e));
  }, [base]);

  const togglePhone = async () => {
    setBusy(true);
    try {
      if (s.here) {
        await disablePush(`${base}/push/unsubscribe`);
        setS((x) => ({ ...x, here: false, devices: Math.max(0, x.devices - 1) }));
        toast.success("Phone alerts off on this device");
      } else {
        const perm = await enablePush((sub) => api.post(`${base}/push/subscribe`, { subscription: sub }));
        if (perm !== "granted") throw new Error("Notifications are blocked in your browser settings");
        setS((x) => ({ ...x, here: true, devices: x.devices + 1 }));
        toast.success(`You'll get a phone alert when ${name} hearts a note`);
      }
    } catch (e) { toast.error(e.message && !e.response ? e.message : errMsg(e)); } finally { setBusy(false); }
  };
  const toggleEmail = async (v) => {
    setS((x) => ({ ...x, heart_email: v }));
    await api.post(`${base}/heart-email`, { enabled: v }).catch((e) => { setS((x) => ({ ...x, heart_email: !v })); toast.error(errMsg(e)); });
  };

  if (!s) return null;
  return (
    <div className="mm-card p-5" data-testid="supporter-heart-alerts">
      <div className="font-semibold flex items-center gap-2"><BellRing size={18} /> Heart alerts</div>
      <div className="text-sm text-mm-muted mt-1">Know right away when {name} hearts one of your notes.</div>
      {pushSupported() ? (
        <button onClick={togglePhone} disabled={busy} className={`${s.here ? "mm-btn-ghost" : "mm-btn"} !py-2 !px-4 text-sm inline-flex items-center gap-2 mt-4`} data-testid="supporter-heart-push-button">
          {busy && <Loader2 size={14} className="animate-spin" />}{s.here ? "Turn off phone alerts on this device" : "Get a phone alert when they heart a note"}
        </button>
      ) : <p className="text-xs text-mm-muted mt-3" data-testid="supporter-heart-push-unsupported">This browser can't show notifications. On iPhone, add this page to your Home Screen first.</p>}
      {s.devices > 0 && (
        <label className="flex items-center justify-between gap-3 mt-4 pt-4 border-t text-sm">
          <span className="text-mm-muted">Also email me when they heart a note</span>
          <Switch checked={s.heart_email} onCheckedChange={toggleEmail} data-testid="supporter-heart-email-toggle" />
        </label>
      )}
    </div>
  );
};
